import gsap from 'gsap';

export default function initResourceFilter() {
  document.querySelectorAll('.resource-ajax-grid').forEach(section => {
    const filterForm = section.querySelector('[data-filter-form]');
    const filterBtn = section.querySelector('[data-filter-btn]');
    const filterDrop = section.querySelector('[data-filter-drop]');
    const filterBar = section.querySelector('[data-filter-bar]');
    const filterMobileBtn = section.querySelector('[data-filter-panel-btn]');
    const filterMobile = section.querySelector('[data-filter-panel]');
    const filterMobileCloseBtns = section.querySelectorAll('[data-filter-panel-close]');
    const clearBtn = section.querySelector('[data-clear-all]');
    const checkboxes = section.querySelectorAll('input[type="checkbox"]');
    const searchInput = section.querySelector('[data-filter-search]');
    const formHolder = section.querySelector('[data-form-holder]');
    const filterDropWrap = section.querySelector('[data-filter-drop-wrap]');
    const filterSearchBar = section.querySelector('[data-filter-search-bar]');
    const filtersCountEls = section.querySelectorAll('[data-filters-count]');
    const ajaxContent = section.querySelector('[data-ajax-content]');
    const preloader = section.querySelector('[data-preloader]');

    const desktopMQ = window.matchMedia('(min-width: 1024px)');

    const classOpen = 'is-open';
    const classActive = 'is-active';
    const classBusy = 'is-busy';

    let isBusy;
    let submittedSearch = '';

    function openFilterDrop() {
      filterBtn.setAttribute('aria-expanded', 'true');
      showEl(filterDrop);
      filterForm.classList.add(classOpen);
      gsap.fromTo(filterDrop, { autoAlpha: 0, y: -5 }, { autoAlpha: 1, y: 0 });
    }

    function closeFilterDrop() {
      filterBtn.setAttribute('aria-expanded', 'false');
      filterForm.classList.remove(classOpen);
      gsap.to(filterDrop, { autoAlpha: 0, y: -5, onComplete() {
        hideEl(filterDrop);
      }});
    }

    let isMobileAnimating = false;

    function getFocusable() {
      return Array.from(filterBar.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )).filter(el => !el.closest('[inert]') && !el.hasAttribute('disabled'));
    }

    function trapFocus(e) {
      if (e.key !== 'Tab' || !filterBar.classList.contains('is-open')) return;
      const focusable = getFocusable();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    function openFilterMobile() {
      filterBar.classList.add('is-open');
      filterMobileBtn.setAttribute('aria-expanded', 'true');
      showEl(filterMobile);
      isMobileAnimating = true;
      gsap.killTweensOf(filterMobile);
      gsap.fromTo(filterMobile,
        { height: 0, overflow: 'hidden' },
        { height: 'auto', duration: 0.4, clearProps: 'height,overflow', onComplete() {
          isMobileAnimating = false;
        }}
      );
    }

    function closeFilterMobile() {
      filterMobileBtn.setAttribute('aria-expanded', 'false');
      filterBar.classList.remove('is-open');
      isMobileAnimating = true;
      gsap.killTweensOf(filterMobile);
      gsap.set(filterMobile, { height: filterMobile.scrollHeight, overflow: 'hidden' });
      gsap.to(filterMobile, { height: 0, duration: 0.4, onComplete() {
        hideEl(filterMobile);
        isMobileAnimating = false;
        filterMobileBtn.focus();
      }});
    }

    function hideEl(el) {
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
    }

    function showEl(el) {
      el.removeAttribute('inert');
      el.setAttribute('aria-hidden', 'false');
    }

    function reorderFormFields() {
      if (!formHolder || !filterDropWrap || !filterSearchBar) return;
      if (desktopMQ.matches) {
        formHolder.appendChild(filterSearchBar);
      } else {
        formHolder.insertBefore(filterSearchBar, filterDropWrap);
      }
    }

    function updateClearBtns() {
      const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length;
      const hasActive = checkedCount > 0 || submittedSearch !== '';
      clearBtn.setAttribute('aria-disabled', String(!hasActive));
      clearBtn.classList.toggle(classActive, hasActive);
      if (hasActive) {
        clearBtn.removeAttribute('tabindex');
      } else {
        clearBtn.setAttribute('tabindex', '-1');
      }
      const countText = checkedCount > 0 ? ` (+${checkedCount})` : '';
      filtersCountEls.forEach(el => { el.textContent = countText; });
    }

    function getFilterUrl() {
      const data = new FormData(filterForm);
      const params = new URLSearchParams();

      const topics = data.getAll('topics');
      if (topics.length) params.set('ftopic', topics.join(','));

      const search = data.get('search');
      if (search) params.set('search', search);

      const query = params.toString();
      return `${filterForm.action}${query ? '?' + query : ''}`;
    }

    async function sendAjax(url, scroll = false) {
      if (isBusy) return;
      isBusy = true;

      const urlObj = new URL(url, location.href);
      history.replaceState(null, '', urlObj.pathname + urlObj.search);

      filterForm.classList.add(classBusy);
      ajaxContent.classList.add(classBusy);
      gsap.to(ajaxContent, { autoAlpha: 0.4 });
      gsap.set(preloader, { autoAlpha: 1 });

      try {
        const ajaxUrl = new URL(url, location.href);
      const ajaxParams = new URLSearchParams([['ajax', '1'], ...ajaxUrl.searchParams]);
      ajaxUrl.search = ajaxParams.toString();

      const res = await fetch(ajaxUrl.toString(), {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store'
        });
        if (!res.ok) throw new Error(res.statusText);
        const html = await res.text();

        if (!desktopMQ.matches || scroll) section.scrollIntoView({ behavior: 'smooth', block: 'start' });

        gsap.to(ajaxContent, { autoAlpha: 0, onComplete() {
          ajaxContent.innerHTML = html;
          gsap.set(preloader, { autoAlpha: 0 });
          gsap.fromTo(ajaxContent, { autoAlpha: 0 }, { autoAlpha: 1 , onComplete: ()=>{
            filterForm.classList.remove(classBusy);
            ajaxContent.classList.remove(classBusy);
            isBusy = false;
          }});
        }});
      } catch {
        gsap.to(ajaxContent, { autoAlpha: 1 });
        gsap.set(preloader, { autoAlpha: 0 });
        filterForm.classList.remove(classBusy);
        ajaxContent.classList.remove(classBusy);
        isBusy = false;
      }
    }

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    filterBar.before(sentinel);

    const stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (desktopMQ.matches) return;
        filterBar.classList.toggle('is-sticky', !entry.isIntersecting);
      },
      { rootMargin: '-68px 0px 0px 0px', threshold: 0 }
    );
    stickyObserver.observe(sentinel);

    reorderFormFields();
    gsap.set(preloader, { autoAlpha: 0 });

    if (desktopMQ.matches) {
      gsap.set(filterDrop, { autoAlpha: 0 });
      showEl(filterMobile);
    } else {
      showEl(filterDrop);
      gsap.set(filterMobile, { height: 0, overflow: 'hidden' });
    }

    updateClearBtns();

    filterBtn.addEventListener('click', () => {
      filterForm.classList.contains(classOpen) ? closeFilterDrop() : openFilterDrop();
    });

    filterDrop.addEventListener('focusout', e => {
      if (!desktopMQ.matches || !e.relatedTarget) return;
      if (e.relatedTarget === filterBtn) return;
      if (!filterDrop.contains(e.relatedTarget)) closeFilterDrop();
    });

    document.addEventListener('click', e => {
      if (!desktopMQ.matches || !filterForm.classList.contains(classOpen)) return;
      if (!filterDrop.contains(e.target) && !filterBtn.contains(e.target)) closeFilterDrop();
    });

    filterMobileBtn.addEventListener('click', () => {
      if (isMobileAnimating) return;
      filterMobileBtn.getAttribute('aria-expanded') === 'true' ? closeFilterMobile() : openFilterMobile();
    });

    filterBar.addEventListener('keydown', trapFocus);

    filterMobileCloseBtns.forEach(btn => btn.addEventListener('click', closeFilterMobile));

    clearBtn.addEventListener('click', () => {
      if (clearBtn.getAttribute('aria-disabled') === 'true') return;
      checkboxes.forEach(cb => { cb.checked = false; });
      searchInput.value = '';
      submittedSearch = '';
      updateClearBtns();
      if (!desktopMQ.matches) closeFilterMobile();
      sendAjax(filterForm.action);
    });

    filterForm.addEventListener('submit', e => {
      e.preventDefault();
      submittedSearch = searchInput.value;
      updateClearBtns();
      sendAjax(getFilterUrl());
    });

    checkboxes.forEach(cb => cb.addEventListener('change', () => {
      updateClearBtns();
      sendAjax(getFilterUrl());
    }));

    ajaxContent.addEventListener('click', e => {
      const link = e.target.closest('a.page-btn');
      if (link && link.getAttribute('aria-disabled') !== 'true') {
        e.preventDefault();
        sendAjax(link.href, true);
        return;
      }

      const emptyStateClear = e.target.closest('[data-clear-all]');
      if (emptyStateClear) {
        e.preventDefault();
        checkboxes.forEach(cb => { cb.checked = false; });
        searchInput.value = '';
        submittedSearch = '';
        updateClearBtns();
        sendAjax(filterForm.action);
      }
    });

    desktopMQ.addEventListener('change', e => {
      filterMobileBtn.setAttribute('aria-expanded', 'false');
      gsap.killTweensOf(filterMobile);
      gsap.killTweensOf(filterDrop);

      if (e.matches) {
        filterBar.classList.remove('is-sticky');
        filterBar.classList.remove('is-open');
        gsap.set(filterMobile, { clearProps: 'height,overflow' });
        gsap.set(filterDrop, { autoAlpha: 0, y: 0 });
        showEl(filterMobile);
        hideEl(filterDrop);
        filterBtn.setAttribute('aria-expanded', 'false');
        filterForm.classList.remove(classOpen);
      } else {
        gsap.set(filterDrop, { clearProps: 'all' });
        gsap.set(filterMobile, { height: 0, overflow: 'hidden' });
        showEl(filterDrop);
        hideEl(filterMobile);
        if (filterForm.classList.contains(classOpen)) {
          filterForm.classList.remove(classOpen);
          filterBtn.setAttribute('aria-expanded', 'false');
        }
      }

      reorderFormFields();
    });
  });
}
